/**
 * Authentication Service - Handle Supabase authentication and sync
 */

import logger from '../core/logger.js';
import { logEvent } from '../core/event-logger.js';
import { EVENT_TYPES } from '../core/event-types.js';
import appState from '../core/app-state.js';
import { i18nService } from './i18n-service.js';
import * as StorageService from './storage-service.js';
import { Role, type User } from '../types/rbac.js';
import { rbacService } from './rbac-service.js';

/**
 * Supabase User type (minimal interface)
 */
interface SupabaseUser {
  id: string;
  email?: string;
  user_metadata?: {
    name?: string;
    [key: string]: any;
  };
  app_metadata?: {
    role?: string;
    [key: string]: any;
  };
  [key: string]: any;
}

/**
 * Supabase Session type
 */
interface SupabaseSession {
  user: SupabaseUser;
  access_token: string;
  refresh_token?: string;
  [key: string]: any;
}

/**
 * Supabase Auth State Change Event
 */
type AuthChangeEvent = 
  | 'SIGNED_IN' 
  | 'SIGNED_OUT' 
  | 'TOKEN_REFRESHED' 
  | 'USER_UPDATED' 
  | 'PASSWORD_RECOVERY';

/**
 * Toast notification callback
 */
type ShowToastCallback = (message: string, type: 'success' | 'error' | 'info', duration?: number) => void;

/**
 * Supabase Client interface (minimal)
 */
interface SupabaseClient {
  auth: {
    getSession(): Promise<{ data: { session: SupabaseSession | null } }>;
    signInWithOtp(options: { email: string; options?: { emailRedirectTo?: string } }): Promise<{ error: any | null }>;
    signUp(options: { email: string; password: string; options?: { emailRedirectTo?: string } }): Promise<{ data: { user: SupabaseUser | null; session: SupabaseSession | null }; error: any | null }>;
    signInWithPassword(options: { email: string; password: string }): Promise<{ data: { user: SupabaseUser | null; session: SupabaseSession | null }; error: any | null }>;
    resetPasswordForEmail(email: string, options?: { redirectTo?: string }): Promise<{ error: any | null }>;
    updateUser(attributes: { password?: string; email?: string; data?: any }): Promise<{ data: { user: SupabaseUser | null }; error: any | null }>;
    signOut(): Promise<{ error: any | null }>;
    onAuthStateChange(callback: (event: AuthChangeEvent, session: SupabaseSession | null) => void): { data: { subscription: any } };
  };
  from(table: string): any;
}

/**
 * Supabase config type
 */
interface SupabaseConfig {
  url: string;
  anonKey: string;
}

/**
 * Global Supabase type declaration
 */
declare global {
  const supabase: {
    createClient(url: string, key: string): SupabaseClient;
  } | undefined;
}

/**
 * Update Auth UI callback type
 */
type UpdateAuthUICallback = () => void;

/**
 * AuthService class - Manages Supabase authentication and data sync
 */
class AuthService {
  private supabaseClient: SupabaseClient | null = null;
  private currentSession: SupabaseSession | null = null;

  /**
   * Initialize Supabase client
   */
  async initialize(): Promise<void> {
    try {
      // Load Supabase config
      const response = await fetch('config/supabase.json');
      const config: SupabaseConfig = await response.json();
      
      // Check if config is placeholder
      if (config.url === 'https://YOUR-PROJECT.supabase.co' || !config.url || !config.anonKey) {
        logger.info('supabase: Config not set up, running in local-only mode');
        return;
      }
      
      // Initialize client
      // Note: Depends on global 'supabase' object loaded from CDN script in index.html
      if (typeof supabase === 'undefined') {
        logger.warn('supabase: Supabase library not loaded, running in local-only mode');
        return;
      }
      
      this.supabaseClient = supabase.createClient(config.url, config.anonKey);
      
      // Check for existing session
      const { data: { session } } = await this.supabaseClient.auth.getSession();
      this.currentSession = session;
      
      if (session) {
        logger.info('supabase: User session restored', { email: session.user.email });
        
        // Update app state with user and role
        this.updateAppStateUser();
        
        // Sync on initial load if signed in (no local save callback needed here - data is already local)
        await this.syncPreferences();
        await this.syncProgress();
      }
      
      // Listen for auth state changes
      this.supabaseClient.auth.onAuthStateChange((event, session) => {
        logger.info('supabase: Auth state changed', { event });
        this.currentSession = session;
        
        if (event === 'SIGNED_IN' && session) {
          logger.info('supabase: User signed in', { email: session.user.email });
          
          // Update app state with user and role
          this.updateAppStateUser();
          
          // Sync after sign-in (no local save callback needed - will be handled by caller)
          this.syncPreferences();
          this.syncProgress();
        } else if (event === 'SIGNED_OUT') {
          logger.info('supabase: User signed out');
          
          // Clear user from app state
          appState.currentUser = null;
        }
      });
      
    } catch (err) {
      logger.warn('supabase: Failed to initialize', err);
    }
  }

  /**
   * Get current user (synchronous, uses cached session)
   * @returns Current user object or null
   */
  getCurrentUser(): SupabaseUser | null {
    if (!this.supabaseClient || !this.currentSession) return null;
    return this.currentSession.user || null;
  }

  /**
   * Convert Supabase user to RBAC User type
   * @param supabaseUser - Supabase user object
   * @returns User object with role (null for regular users)
   */
  private convertToRbacUser(supabaseUser: SupabaseUser): User {
    // Get role from app_metadata (server-controlled, non-user-writable)
    // No role = regular app user (not an admin/editor/reviewer)
    const roleString = supabaseUser.app_metadata?.role;
    const role = roleString && rbacService.isValidRole(roleString) ? (roleString as Role) : null;
    
    return {
      id: supabaseUser.id,
      email: supabaseUser.email,
      role: role,
      name: supabaseUser.user_metadata?.name,
      metadata: supabaseUser.user_metadata
    };
  }

  /**
   * Get current user with RBAC role information
   * @returns User object with role or null
   */
  getCurrentUserWithRole(): User | null {
    const supabaseUser = this.getCurrentUser();
    if (!supabaseUser) return null;
    
    return this.convertToRbacUser(supabaseUser);
  }

  /**
   * Get current auth token (access token)
   * @returns Access token or null
   */
  getAuthToken(): string | null {
    if (!this.currentSession) return null;
    return this.currentSession.access_token || null;
  }

  /**
   * Update app state with current user
   */
  private updateAppStateUser(): void {
    appState.currentUser = this.getCurrentUserWithRole();
    logger.debug('auth: Updated appState.currentUser', { 
      userId: appState.currentUser?.id,
      role: appState.currentUser?.role 
    });
  }

  /**
   * Sign in with magic link (DEPRECATED - Use signInWithPassword instead)
   * @param email - User's email address
   * @param showToast - Toast notification function
   */
  async signIn(email: string, showToast: ShowToastCallback): Promise<void> {
    if (!this.supabaseClient) {
      logger.warn('supabase: Supabase not initialized');
      showToast(i18nService.getString('failedToSend') || 'Sign-in not available. Supabase not configured.', 'error');
      return;
    }
    
    try {
      const { error } = await this.supabaseClient.auth.signInWithOtp({
        email: email,
        options: {
          emailRedirectTo: window.location.origin
        }
      });
      
      if (error) {
        logger.error('supabase: Sign-in error', error);
        showToast(i18nService.getString('failedToSend') || 'Failed to send sign-in link. Please try again.', 'error');
        return;
      }
      
      // Show success message
      showToast(i18nService.getString('sentLink') || 'Sign-in link sent! Check your email.', 'success');
      
      logEvent('auth_sign_in_requested', { email });
    } catch (err) {
      logger.error('supabase: Sign-in error', err);
      showToast(i18nService.getString('failedToSend') || 'Failed to send sign-in link. Please try again.', 'error');
    }
  }

  /**
   * Sign up with email and password
   * @param email - User's email address
   * @param password - User's password
   * @param showToast - Toast notification function
   */
  async signUp(email: string, password: string, showToast: ShowToastCallback): Promise<void> {
    if (!this.supabaseClient) {
      logger.warn('supabase: Supabase not initialized');
      showToast(i18nService.getString('signUpFailed') || 'Sign-up not available. Supabase not configured.', 'error');
      return;
    }
    
    try {
      const { data, error } = await this.supabaseClient.auth.signUp({
        email: email,
        password: password,
        options: {
          emailRedirectTo: window.location.origin
        }
      });
      
      if (error) {
        logger.error('supabase: Sign-up error', error);
        
        // Handle specific error messages
        if (error.message?.includes('already registered')) {
          showToast(i18nService.getString('emailAlreadyExists') || 'This email is already registered. Please sign in instead.', 'error');
        } else if (error.message?.includes('Password should be')) {
          showToast(i18nService.getString('weakPassword') || 'Password is too weak. Please use a stronger password.', 'error');
        } else {
          showToast(i18nService.getString('signUpFailed') || 'Failed to create account. Please try again.', 'error');
        }
        return;
      }
      
      // Check if email confirmation is required
      if (data.user && !data.session) {
        showToast(i18nService.getString('confirmEmailSent') || 'Account created! Please check your email to confirm your account.', 'success', 5000);
      } else {
        showToast(i18nService.getString('signUpSuccess') || 'Account created successfully!', 'success');
      }
      
      logEvent(EVENT_TYPES.AUTH_SIGN_UP, { email });
    } catch (err) {
      logger.error('supabase: Sign-up error', err);
      showToast(i18nService.getString('signUpFailed') || 'Failed to create account. Please try again.', 'error');
    }
  }

  /**
   * Sign in with email and password
   * @param email - User's email address
   * @param password - User's password
   * @param showToast - Toast notification function
   */
  async signInWithPassword(email: string, password: string, showToast: ShowToastCallback): Promise<void> {
    if (!this.supabaseClient) {
      logger.warn('supabase: Supabase not initialized');
      showToast(i18nService.getString('signInFailed') || 'Sign-in not available. Supabase not configured.', 'error');
      return;
    }
    
    try {
      const { data, error } = await this.supabaseClient.auth.signInWithPassword({
        email: email,
        password: password
      });
      
      if (error) {
        logger.error('supabase: Sign-in with password error', error);
        
        // Handle specific error messages
        if (error.message?.includes('Invalid login credentials')) {
          showToast(i18nService.getString('invalidCredentials') || 'Invalid email or password. Please try again.', 'error');
        } else if (error.message?.includes('Email not confirmed')) {
          showToast(i18nService.getString('emailNotConfirmed') || 'Please confirm your email address before signing in.', 'error');
        } else {
          showToast(i18nService.getString('signInFailed') || 'Failed to sign in. Please try again.', 'error');
        }
        return;
      }
      
      if (data.session) {
        showToast(i18nService.getString('signInSuccess') || 'Signed in successfully!', 'success');
        logEvent(EVENT_TYPES.AUTH_SIGN_IN, { email });
      }
    } catch (err) {
      logger.error('supabase: Sign-in with password error', err);
      showToast(i18nService.getString('signInFailed') || 'Failed to sign in. Please try again.', 'error');
    }
  }

  /**
   * Request password reset email
   * @param email - User's email address
   * @param showToast - Toast notification function
   */
  async requestPasswordReset(email: string, showToast: ShowToastCallback): Promise<void> {
    if (!this.supabaseClient) {
      logger.warn('supabase: Supabase not initialized');
      showToast(i18nService.getString('resetFailed') || 'Password reset not available. Supabase not configured.', 'error');
      return;
    }
    
    try {
      const { error } = await this.supabaseClient.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}?mode=reset`
      });
      
      if (error) {
        logger.error('supabase: Password reset request error', error);
        showToast(i18nService.getString('resetFailed') || 'Failed to send password reset email. Please try again.', 'error');
        return;
      }
      
      showToast(i18nService.getString('resetEmailSent') || 'Password reset email sent! Check your email.', 'success', 5000);
      logEvent(EVENT_TYPES.AUTH_PASSWORD_RESET_REQUESTED, { email });
    } catch (err) {
      logger.error('supabase: Password reset request error', err);
      showToast(i18nService.getString('resetFailed') || 'Failed to send password reset email. Please try again.', 'error');
    }
  }

  /**
   * Set new password (for recovery flow)
   * @param newPassword - New password
   * @param showToast - Toast notification function
   */
  async setNewPassword(newPassword: string, showToast: ShowToastCallback): Promise<void> {
    if (!this.supabaseClient) {
      logger.warn('supabase: Supabase not initialized');
      showToast(i18nService.getString('resetFailed') || 'Password reset not available. Supabase not configured.', 'error');
      return;
    }
    
    try {
      const { data, error } = await this.supabaseClient.auth.updateUser({
        password: newPassword
      });
      
      if (error) {
        logger.error('supabase: Set new password error', error);
        
        if (error.message?.includes('Password should be')) {
          showToast(i18nService.getString('weakPassword') || 'Password is too weak. Please use a stronger password.', 'error');
        } else {
          showToast(i18nService.getString('resetFailed') || 'Failed to reset password. Please try again.', 'error');
        }
        return;
      }
      
      if (data.user) {
        showToast(i18nService.getString('resetSuccess') || 'Password reset successfully! You are now signed in.', 'success');
        logEvent(EVENT_TYPES.AUTH_PASSWORD_RESET_COMPLETED, {});
      }
    } catch (err) {
      logger.error('supabase: Set new password error', err);
      showToast(i18nService.getString('resetFailed') || 'Failed to reset password. Please try again.', 'error');
    }
  }

  /**
   * Sign out current user
   * @param showToast - Toast notification function
   */
  async signOut(showToast: ShowToastCallback): Promise<void> {
    if (!this.supabaseClient) {
      logger.warn('supabase: Supabase not initialized');
      return;
    }
    
    try {
      const { error } = await this.supabaseClient.auth.signOut();
      
      if (error) {
        logger.error('supabase: Sign-out error', error);
        return;
      }
      
      logger.info('supabase: User signed out');
      logEvent('auth_sign_out', {});
      
      // Show success message
      showToast(i18nService.getString('signOutSuccess') || 'Signed out successfully.', 'success');
    } catch (err) {
      logger.error('supabase: Sign-out error', err);
    }
  }

  /**
   * Sync preferences to server
   */
  async syncPreferences(): Promise<void> {
    if (!this.supabaseClient) return;
    
    const user = this.getCurrentUser();
    if (!user) return;
    
    try {
      // Fetch server preferences
      const { data: serverPrefs, error: fetchError } = await this.supabaseClient
        .from('preferences')
        .select('*')
        .eq('user_id', user.id)
        .single();
      
      if (fetchError && fetchError.code !== 'PGRST116') { // PGRST116 = not found
        logger.error('supabase: Failed to fetch preferences', fetchError);
        return;
      }
      
      const localPrefs = appState.preferences;
      const now = new Date().toISOString();
      
      if (!serverPrefs) {
        // No server data, push local
        const { error: insertError } = await this.supabaseClient
          .from('preferences')
          .insert({
            user_id: user.id,
            ui_language: localPrefs['uiLanguage'] || 'en',
            cefr_level: localPrefs['cefrLevel'] || 'B2',
            daily_goal: localPrefs['dailyGoal'] || 1,
            learning_language: localPrefs['learningLanguage'] || 'tr',
            updated_at: now
          });
        
        if (insertError) {
          logger.error('supabase: Failed to insert preferences', insertError);
        } else {
          logger.info('supabase: Preferences pushed to server');
        }
      } else {
        // Merge: server wins if newer, otherwise push local
        const serverUpdated = new Date(serverPrefs.updated_at);
        const localUpdated = new Date((localPrefs['updatedAt'] as string | number) || 0);
        
        if (serverUpdated > localUpdated) {
          // Server is newer, pull
          appState.preferences['uiLanguage'] = serverPrefs.ui_language;
          appState.preferences['cefrLevel'] = serverPrefs.cefr_level;
          appState.preferences['dailyGoal'] = serverPrefs.daily_goal ?? 1;
          appState.preferences['learningLanguage'] = serverPrefs.learning_language ?? 'tr';
          appState.preferences['updatedAt'] = serverPrefs.updated_at;
          
          // Save updated preferences to localStorage
          StorageService.setJSON('listen-up-preferences', appState.preferences);
          
          logger.info('supabase: Preferences pulled from server');
        } else {
          // Local is newer or same, push
          const { error: updateError } = await this.supabaseClient
            .from('preferences')
            .update({
              ui_language: localPrefs['uiLanguage'] || 'en',
              cefr_level: localPrefs['cefrLevel'] || 'B2',
              daily_goal: localPrefs['dailyGoal'] || 1,
              learning_language: localPrefs['learningLanguage'] || 'tr',
              updated_at: now
            })
            .eq('user_id', user.id);
          
          if (updateError) {
            logger.error('supabase: Failed to update preferences', updateError);
          } else {
            logger.info('supabase: Preferences pushed to server');
          }
        }
      }
    } catch (err) {
      logger.error('supabase: Sync preferences error', err);
    }
  }

  /**
   * Sync progress to server
   */
  async syncProgress(): Promise<void> {
    if (!this.supabaseClient) return;
    
    const user = this.getCurrentUser();
    if (!user) return;
    
    try {
      // Fetch server progress
      const { data: serverProgress, error: fetchError } = await this.supabaseClient
        .from('progress')
        .select('*')
        .eq('user_id', user.id);
      
      if (fetchError) {
        logger.error('supabase: Failed to fetch progress', fetchError);
        return;
      }
      
      const localProgress = appState.progressMap;
      const now = new Date().toISOString();
      
      // Build server progress map
      const serverMap: Record<string, { status: string; updated_at: string }> = {};
      if (serverProgress) {
        serverProgress.forEach((item: any) => {
          serverMap[item.clip_id] = {
            status: item.status,
            updated_at: item.updated_at
          };
        });
      }
      
      // Merge: combine both and keep newest
      const merged: Record<string, boolean> = { ...localProgress };
      const toUpsert: any[] = [];
      
      // Check for server items that should override local
      Object.keys(serverMap).forEach(clipId => {
        if (!merged[clipId]) {
          merged[clipId] = serverMap[clipId]!.status === 'completed';
        }
      });
      
      // Prepare upserts for all completed local items
      Object.keys(merged).forEach(clipId => {
        if (merged[clipId]) {
          toUpsert.push({
            user_id: user.id,
            clip_id: clipId,
            status: 'completed',
            updated_at: now
          });
        }
      });
      
      if (toUpsert.length > 0) {
        const { error: upsertError } = await this.supabaseClient
          .from('progress')
          .upsert(toUpsert, { onConflict: 'user_id,clip_id' });
        
        if (upsertError) {
          logger.error('supabase: Failed to upsert progress', upsertError);
        } else {
          logger.info('supabase: Progress synced to server', { count: toUpsert.length });
        }
      }
      
      // Update local with merged and save to localStorage
      appState.progressMap = merged;
      StorageService.setJSON('listenUpProgress', appState.progressMap);
      
    } catch (err) {
      logger.error('supabase: Sync progress error', err);
    }
  }

  /**
   * Initialize Auth UI handlers
   * @param updateAuthUI - Callback to update auth UI state
   * @param showToast - Toast notification function
   */
  initAuthUI(updateAuthUI: UpdateAuthUICallback, showToast: ShowToastCallback): void {
    const accountBtn = document.getElementById('account-btn');
    const authModal = document.getElementById('auth-modal');
    const authModalClose = document.getElementById('auth-modal-close');
    const authForm = document.getElementById('auth-form');
    const authModeToggle = document.getElementById('auth-mode-toggle');
    const forgotPasswordBtn = document.getElementById('forgot-password-btn');
    const authSubmitBtn = document.getElementById('auth-submit-btn');
    const authModalTitle = document.getElementById('auth-modal-title');
    const authModalDescription = document.getElementById('auth-modal-description');
    const authConfirmPasswordGroup = document.getElementById('auth-confirm-password-group');
    const userMenu = document.getElementById('user-menu');
    const signOutBtn = document.getElementById('sign-out-btn');
    
    if (!accountBtn || !authModal || !userMenu) {
      logger.warn('auth-ui: Auth UI elements not found');
      return;
    }
    
    // Track auth modal mode ('signin' or 'signup')
    let authMode: 'signin' | 'signup' = 'signin';
    
    // Helper function to update auth modal for current mode
    const updateAuthModalMode = (mode: 'signin' | 'signup') => {
      authMode = mode;
      
      if (mode === 'signup') {
        if (authModalTitle) authModalTitle.textContent = i18nService.getString('createAccount') || 'Create Account';
        if (authModalDescription) authModalDescription.textContent = i18nService.getString('signUpDescription') || 'Create an account to save your progress across all devices.';
        if (authSubmitBtn) authSubmitBtn.textContent = i18nService.getString('signUp') || 'Sign Up';
        if (authModeToggle) authModeToggle.textContent = i18nService.getString('signIn') || 'Sign In';
        if (authConfirmPasswordGroup) authConfirmPasswordGroup.classList.remove('hidden');
        if (forgotPasswordBtn) forgotPasswordBtn.classList.add('hidden');
        
        // Update password field autocomplete
        const passwordInput = document.getElementById('auth-password') as HTMLInputElement | null;
        if (passwordInput) passwordInput.setAttribute('autocomplete', 'new-password');
      } else {
        if (authModalTitle) authModalTitle.textContent = i18nService.getString('signIn') || 'Sign In';
        if (authModalDescription) authModalDescription.textContent = i18nService.getString('signInDescription') || 'Save your progress across all your devices.';
        if (authSubmitBtn) authSubmitBtn.textContent = i18nService.getString('signIn') || 'Sign In';
        if (authModeToggle) authModeToggle.textContent = i18nService.getString('createAccount') || 'Create Account';
        if (authConfirmPasswordGroup) authConfirmPasswordGroup.classList.add('hidden');
        if (forgotPasswordBtn) forgotPasswordBtn.classList.remove('hidden');
        
        // Update password field autocomplete
        const passwordInput = document.getElementById('auth-password') as HTMLInputElement | null;
        if (passwordInput) passwordInput.setAttribute('autocomplete', 'current-password');
      }
    };
    
    // Account button click
    accountBtn.addEventListener('click', () => {
      const user = this.getCurrentUser();
      
      if (user) {
        // Toggle user menu
        userMenu.classList.toggle('hidden');
        logEvent('ui_interaction', { action: 'user_menu_toggle', open: !userMenu.classList.contains('hidden') });
      } else {
        // Show auth modal in sign-in mode
        authMode = 'signin';
        updateAuthModalMode('signin');
        authModal.classList.remove('hidden');
        const emailInput = document.getElementById('auth-email') as HTMLInputElement | null;
        if (emailInput) {
          setTimeout(() => emailInput.focus(), 100);
        }
        logEvent('ui_interaction', { action: 'auth_modal_open' });
      }
    });
    
    // Close auth modal
    if (authModalClose) {
      authModalClose.addEventListener('click', () => {
        authModal.classList.add('hidden');
        logEvent('ui_interaction', { action: 'auth_modal_close' });
      });
    }
    
    // Click outside to close
    authModal.addEventListener('click', (e) => {
      if (e.target === authModal) {
        authModal.classList.add('hidden');
        logEvent('ui_interaction', { action: 'auth_modal_close', method: 'outside_click' });
      }
    });
    
    // Click outside user menu to close
    document.addEventListener('click', (e) => {
      if (!userMenu.classList.contains('hidden') && 
          !userMenu.contains(e.target as Node) && 
          !accountBtn.contains(e.target as Node)) {
        userMenu.classList.add('hidden');
      }
    });
    
    // Auth mode toggle button
    if (authModeToggle) {
      authModeToggle.addEventListener('click', () => {
        const newMode = authMode === 'signin' ? 'signup' : 'signin';
        updateAuthModalMode(newMode);
        logEvent('ui_interaction', { action: 'auth_mode_toggle', mode: newMode });
      });
    }
    
    // Forgot password button
    if (forgotPasswordBtn) {
      forgotPasswordBtn.addEventListener('click', async () => {
        const emailInput = document.getElementById('auth-email') as HTMLInputElement | null;
        const email = emailInput ? emailInput.value.trim() : '';
        
        if (!email) {
          showToast(i18nService.getString('emailRequired') || 'Please enter your email address.', 'error');
          if (emailInput) emailInput.focus();
          return;
        }
        
        await this.requestPasswordReset(email, showToast);
        logEvent(EVENT_TYPES.AUTH_FORGOT_PASSWORD, { email });
      });
    }
    
    // Auth form submission
    if (authForm) {
      authForm.addEventListener('submit', async (e) => {
        e.preventDefault();
        const emailInput = document.getElementById('auth-email') as HTMLInputElement | null;
        const passwordInput = document.getElementById('auth-password') as HTMLInputElement | null;
        const confirmPasswordInput = document.getElementById('auth-confirm-password') as HTMLInputElement | null;
        
        const email = emailInput ? emailInput.value.trim() : '';
        const password = passwordInput ? passwordInput.value : '';
        const confirmPassword = confirmPasswordInput ? confirmPasswordInput.value : '';
        
        if (!email || !password) return;
        
        if (authMode === 'signup') {
          // Validate passwords match
          if (password !== confirmPassword) {
            showToast(i18nService.getString('passwordsDoNotMatch') || 'Passwords do not match.', 'error');
            if (confirmPasswordInput) confirmPasswordInput.focus();
            return;
          }
          
          await this.signUp(email, password, showToast);
        } else {
          await this.signInWithPassword(email, password, showToast);
        }
        
        authModal.classList.add('hidden');
        if (authForm) (authForm as HTMLFormElement).reset();
      });
    }
    
    // Sign out button
    if (signOutBtn) {
      signOutBtn.addEventListener('click', async () => {
        await this.signOut(showToast);
        userMenu.classList.add('hidden');
        if (typeof updateAuthUI === 'function') {
          updateAuthUI();
        }
      });
    }
    
    // Listen for auth state changes
    if (this.supabaseClient) {
      this.supabaseClient.auth.onAuthStateChange((event, _session) => {
        if (typeof updateAuthUI === 'function') {
          updateAuthUI();
        }
        
        if (event === 'SIGNED_IN') {
          userMenu.classList.add('hidden');
          logger.info('auth-ui: User signed in, UI updated');
        }
      });
    }
    
    // Initial update
    if (typeof updateAuthUI === 'function') {
      updateAuthUI();
    }
    
    logger.info('auth-ui: Auth UI initialized');
  }
}

// Create and export singleton instance
export const authService = new AuthService();

// Export class for testing
export { AuthService };
export type { SupabaseUser, SupabaseSession, ShowToastCallback };
